<template>
  <v-container fluid>
    <v-layout>
      <v-flex>
        <div style="padding-bottom: 8px" class="headline">
          System Health
        </div>
        <v-flex v-if="isError" xs12 py-2>
          <span class="error--text">An error occurred requesting system health status</span>
        </v-flex>
        <div v-else class="hs-timestamp">
          <span><span class="font-weight-bold">Last Update: </span>{{ normalizedTimeStamp }}</span>
          <span :style="{ color: 'rgba(0,0,0,0.54)' }">Refreshes every 60 seconds</span>
        </div>
      </v-flex>
      <SystemStatusLegend v-if="!isError && $vuetify.breakpoint.xs" />
    </v-layout>

    <template v-if="!isError">
      <div>
        <table class="hs-table">
            <th />
            <th>
              <SystemStatusLegend v-if="!$vuetify.breakpoint.xs" />
            </th>
          <tbody>
            <tr>
              <th>System</th>
              <th>Status</th>
              <th>Description</th>
            </tr>
            <tr class="hs-table__category">
              <td data-th="System">
                Core Services
              </td>
              <td data-th="Status" />
              <td data-th="Description">
                Services that keep HawkSoft and other Cloud Products running
              </td>
            </tr>
            <tr v-for="(system, idx) in coreServices" :key="`${system.id}-${idx}`">
              <td data-th="System">
                {{ system.name }}
              </td>
              <td data-th="Status">
                <v-icon :class="statusStyleEnum[system.status].class">
                  {{ statusStyleEnum[system.status].icon }}
                </v-icon>
              </td>
              <td data-th="Description">
                {{ system.desc }}
              </td>
            </tr>
            <tr class="hs-table__category">
              <td data-th="System">
                Data Sync
              </td>
              <td data-th="Status" />
              <td data-th="Description">
                Synchronizes client and policy data to HawkSoft Cloud
              </td>
            </tr>
            <tr v-for="(system, idx) in dataSync" :key="`${system.id}-${idx}`">
              <td data-th="System">
                {{ system.name }}
              </td>
              <td data-th="Status">
                <v-icon :class="statusStyleEnum[system.status].class">
                  {{ statusStyleEnum[system.status].icon }}
                </v-icon>
              </td>
              <td data-th="Description">
                {{ system.desc }}
              </td>
            </tr>
            <tr class="hs-table__category">
              <td data-th="System">
                Products
              </td>
              <td data-th="Status" />
              <td data-th="Description">
                HawkSoft Products and Services
              </td>
            </tr>
            <tr v-for="(system, idx) in products" :key="`${system.id}-${idx}`">
              <td data-th="System">
                {{ system.name }}
              </td>
              <td data-th="Status">
                <v-icon :class="statusStyleEnum[system.status].class">
                  {{ statusStyleEnum[system.status].icon }}
                </v-icon>
              </td>
              <td data-th="Description">
                {{ system.desc }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </v-container>
</template>

<script>
import moment from 'moment';
import { statusHandler } from '@/services/api';

import SystemStatusLegend from '@/components/fragments/SystemStatusLegend.vue';

export default {
  components: {
    SystemStatusLegend
  },
  data () {
    return {
      normalizedTimeStamp: '',
      isError: false,
      isLoading: false,
      systemStatusData: [],
      statusStyleEnum: {
        healthy: {
          icon: 'check_circle',
          class: 'green--text'
        },
        offline: {
          icon: 'cancel',
          class: 'red--text'
        },
        degraded: {
          icon: 'error',
          class: 'amber--text text--darken-2'
        }
      }
    };
  },
  computed: {
    coreServices () {
      const r = this.systemStatusData;
      const formatted = [
        {
          id: 0,
          name: 'Start Configuration',
          status: this.isSystemHealthy(r.CmsConfig),
          desc: 'Ensures HawkSoft CMS has all the files it needs to run and work with Cloud Services'
        }, {
          id: 1,
          name: 'Subscription Service',
          status: this.isSystemHealthy(r.Subscription),
          desc: 'Maintains agency subscription to HawkSoft CMS'
        }, {
          id: 2,
          name: 'Agency Configuration Sync',
          status: this.isSystemHealthy(r.Receive, r.AgencyConfigFunction),
          desc: 'Keeps agency information set in HawkSoft CMS in sync with Cloud Services and Products'
        }, {
          id: 3,
          name: 'Cloud Search',
          status: this.isSystemHealthy(r.Search),
          desc: 'Cloud-based search that includes location searching (AKA “New Search Experience”)'
        }
      ];
      return formatted;
    },
    dataSync () {
      const r = this.systemStatusData;
      const formatted = [
        {
          id: 5,
          name: 'Cloud Storage Access',
          status: this.isSystemHealthy(r.StorageKeys),
          desc: 'Access to Agency storage in HawkSoft Cloud'
        }, {
          id: 6,
          name: 'Cloud Storage Read/Write',
          status: this.isSystemHealthy(r.WritingToStorage),
          desc: 'Grants agency access to read or write client and policy data to HawkSoft Cloud'
        }
      ];
      return formatted;
    },
    products () {
      const r = this.systemStatusData;
      const formatted = [
        {
          id: 7,
          name: 'Agent Portal',
          status: this.isSystemHealthy(r.AgentAPI, r.AgentPortal),
          desc: 'Access client and policy data from a mobile device or web browser'
        }
      ];
      return formatted;
    }
  },
  created () {
    this.pollSystemStatus();
  },
  methods: {
    // first execution will be immediate, thereafter all calls' `interval` param will be set to 1 min
    pollSystemStatus (interval) {
      setTimeout(async () => {
        this.isLoading = true;
        try {
          let observedData = await statusHandler.getSystemStatus();
          if (!observedData) {
            this.isError = true;
          } else {
            this.systemStatusData = observedData;
            this.generateTimeStamp();
          }
        } catch (err) {
          this.isError = true;
        } finally {
          this.isLoading = false;
          this.pollSystemStatus(60000);
        }
      }, 0 || interval);
    },
    isSystemHealthy (...systems) {
      // if status is contingent on more than one service
      if (systems.length > 1) {
        return systems.every(system => system === 'Healthy') ? 'healthy' : 'offline';
      }
      // single service; if the status is something other than healthy or degraded, default to offline
      return systems[0] !== 'Healthy' ? systems[0] === 'Degraded' ? 'degraded' : 'offline' : 'healthy';
    },
    generateTimeStamp () {
      this.normalizedTimeStamp = moment().format('h:mm:ss a');
    }
  }
};
</script>

<style scoped>

  .hs-table {
    margin: auto;
    min-width: 300px;
    max-width: 100%;
    border-collapse: collapse;
  }

  .hs-table tr:first-child {
    border-top: none;
    color: rgba(0,0,0,.54);
    font-size: 12px;
  }

  .hs-table tr {
    border-top: 1px solid #ddd;
    border-bottom: 1px solid #ddd;
  }

  .hs-table th {
    display: none;
    font-weight: 300;
  }

  .hs-table th:nth-child(even) {
    text-align: center;
  }
  .hs-table th:last-child {
    text-align: left;
  }

  .hs-table td {
    display: block;
  }

  .hs-table td:first-child {
    margin-top: .5em;
  }

  .hs-table td:last-child {
    margin-bottom: .5em;
  }

  .hs-table td:before {
    content: attr(data-th) ": ";
    font-weight: bold;
    width: 120px;
    display: inline-block;
    color: #000;
  }

  .hs-table th,
  .hs-table td {
    text-align: left;
  }

  .hs-table {
    color: #333;
    overflow: hidden;
  }

  .hs-table tr {
    border-color: #bfbfbf;
  }

  .hs-table th,
  .hs-table td {
    padding: .5em 1em;
  }

  .hs-timestamp {
    display: inline;
    font-size: 16px;
  }

  .hs-timestamp span:not(:first-child) {
    display: block;
  }

  /* Tablet and Mobile */
  @media screen and (max-width: 650px) {
    .hs-table {
      margin-top: 16px;
    }
    .container {
      padding: 6px !important;
    }
    .hs-table tr:nth-child(2) {
      border-top: none;
    }
    .hs-table td:last-child {
      white-space: pre-line;
    }
    .hs-table tr:nth-child(odd):not(:first-child) {
      background-color: #ebf3f9;
    }
    .hs-table tr:first-child {
      background: #f5f9fc;
    }
    .hs-table tr {
      background-color: #f5f9fc;
    }
    .hs-table__category {
      display: none;
    }
    .hs-timestamp span:nth-of-type(2) {
      display: none;
    }
  }

  /* Large Viewport */
  @media screen and (min-width: 651px) {
    .hs-table td:before {
      display: none;
    }
    .hs-table th,
    .hs-table td {
      display: table-cell;
      padding: .25em .5em;
    }
    .hs-table th:last-child,
    .hs-table td:last-child {
      padding-right: 0;
    }
    .hs-table th,
    .hs-table td {
      padding: 1em !important;
    }
    .hs-table th:first-child {
      padding-left: 65px !important
    }
    .hs-table td:nth-child(odd):not(:last-child) {
      width: 25%;
      padding-left: 65px !important;
    }
    .hs-table td:nth-child(even) {
      text-align: center;
      width: 33%;
      padding-left: 90px !important;
    }
    .hs-table th:nth-child(even) {
      padding-left: 90px !important;
    }
    .hs-table__category {
      font-size: 16px;
      font-weight: 700;
      background: #ebf3f9;
    }
    .hs-table__category td:last-child {
      font-size: 14px;
    }
  }

</style>
